import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import GridViewIcon from '@mui/icons-material/GridView';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useNavigate } from 'react-router-dom';
import { DS } from '../../theme';

export type AppPage =
  | 'dashboard'
  | 'projects'
  | 'datasets'
  | 'demo'
  | 'explore'
  | 'overview'
  | 'analysis'
  | 'chat'
  | 'manipulation'
  | 'admin';

interface Project {
  id: number;
  name: string;
  dataset_count: number;
}

interface SidebarProps {
  activePage: AppPage;
  onNavigate: (page: AppPage) => void;
  onOpenProject?: (project: Project) => void;
  projects?: Project[];
  selectedProjectId?: number;
  projectName?: string;
  datasetName?: string;
  isAdmin?: boolean;
  hasDataset?: boolean;
}

const NavItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
  indent?: boolean;
}> = ({ icon, label, isActive, onClick, disabled, indent }) => (
  <Tooltip title={disabled ? 'Select a dataset first' : ''} placement="right">
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        px: '10px',
        py: '8px',
        pl: indent ? '20px' : '10px',
        borderRadius: '8px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        backgroundColor: isActive ? DS.accentDim : 'transparent',
        transition: 'background-color 0.15s ease, color 0.15s ease',
        '&:hover': disabled ? {} : {
          backgroundColor: isActive ? DS.accentDim : DS.bg3,
        },
        mb: '2px',
      }}
    >
      <Box sx={{
        color: isActive ? DS.accent : DS.textMuted,
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        opacity: isActive ? 1 : 0.7,
        transition: 'color 0.15s ease',
      }}>
        {icon}
      </Box>
      <Typography
        noWrap
        sx={{
          fontSize: '13.5px',
          fontWeight: isActive ? 500 : 400,
          color: isActive ? DS.accent : 'rgba(255,255,255,0.55)',
          lineHeight: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: indent ? 140 : 160,
          transition: 'color 0.15s ease',
          '&:hover': { color: isActive ? DS.accent : DS.text },
        }}
      >
        {label}
      </Typography>
    </Box>
  </Tooltip>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: '10px',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.28)',
      px: '10px',
      py: '6px',
      mt: '4px',
    }}
  >
    {children}
  </Typography>
);

const Sidebar: React.FC<SidebarProps> = ({
  activePage,
  onNavigate,
  onOpenProject,
  projects = [],
  selectedProjectId,
  projectName,
  datasetName,
  isAdmin,
}) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        width: 220,
        minWidth: 220,
        height: '100%',
        backgroundColor: DS.bg2,
        borderRight: `1px solid ${DS.border}`,
        display: 'flex',
        flexDirection: 'column',
        pb: '24px',
        overflow: 'hidden',
      }}
    >
      {/* ── Logo area ── */}
      <Box
        onClick={() => navigate('/')}
        sx={{
          height: 70,
          minHeight: 70,
          display: 'flex',
          alignItems: 'center',
          px: '18px',
          borderBottom: `1px solid ${DS.border}`,
          flexShrink: 0,
          cursor: 'pointer',
          '&:hover .ds-wordmark': { color: 'rgba(255,255,255,0.75)' },
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            backgroundColor: DS.accent,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ color: '#fff', fontSize: '12px', fontWeight: 700, letterSpacing: '0px' }}>
            DS
          </Typography>
        </Box>
        <Typography
          className="ds-wordmark"
          sx={{
            fontSize: '15px',
            fontWeight: 600,
            color: DS.text,
            letterSpacing: '-0.3px',
            ml: '10px',
            transition: 'color 0.15s',
          }}
        >
          DataScope
        </Typography>
      </Box>

      {/* ── Nav content ── */}
      <Box sx={{ flex: 1, overflow: 'hidden', px: '8px', pt: '8px' }}>

        {/* Context breadcrumb (project/dataset name) */}
        {(projectName || datasetName) && (
          <Box
            sx={{
              px: '10px',
              py: '8px',
              mb: '8px',
              backgroundColor: DS.bg3,
              border: `1px solid ${DS.border}`,
              borderRadius: '8px',
            }}
          >
            {projectName && (
              <Typography sx={{ fontSize: '10px', color: DS.textFaint, fontWeight: 500, mb: '2px' }}>
                {projectName}
              </Typography>
            )}
            {datasetName && (
              <Typography sx={{ fontSize: '12px', color: DS.text, fontWeight: 600, lineHeight: 1.3 }} noWrap>
                {datasetName}
              </Typography>
            )}
          </Box>
        )}

        {/* Workspace */}
        <SectionLabel>Workspace</SectionLabel>
        <NavItem
          icon={<GridViewIcon sx={{ fontSize: 16 }} />}
          label="Home"
          isActive={activePage === 'dashboard'}
          onClick={() => onNavigate('dashboard')}
        />
        <NavItem
          icon={<FolderOpenIcon sx={{ fontSize: 16 }} />}
          label="Projects"
          isActive={activePage === 'projects' && !selectedProjectId}
          onClick={() => onNavigate('projects')}
        />

        {/* Projects list */}
        {projects.length > 0 && (
          <>
            <Box sx={{ height: '1px', backgroundColor: DS.border, mx: '10px', my: '10px' }} />
            <SectionLabel>Projects</SectionLabel>
            {projects.map(project => (
              <NavItem
                key={project.id}
                icon={<FolderOpenIcon sx={{ fontSize: 15 }} />}
                label={project.name}
                isActive={selectedProjectId === project.id}
                onClick={() => onOpenProject?.(project)}
                indent
              />
            ))}
          </>
        )}

        {/* Admin */}
        {isAdmin && (
          <>
            <Box sx={{ height: '1px', backgroundColor: DS.border, mx: '10px', my: '10px' }} />
            <SectionLabel>System</SectionLabel>
            <NavItem
              icon={<AdminPanelSettingsIcon sx={{ fontSize: 16 }} />}
              label="Admin"
              isActive={activePage === 'admin'}
              onClick={() => onNavigate('admin')}
            />
          </>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          mt: 'auto',
          pt: '16px',
          px: '18px',
          borderTop: `1px solid ${DS.border}`,
        }}
      >
        <Typography sx={{ fontSize: '11px', color: DS.textFaint }}>
          DataScope v1.0
        </Typography>
      </Box>
    </Box>
  );
};

export default Sidebar;
